import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  ShieldAlert,
  GraduationCap,
  Church,
  Building2,
  Users,
  CalendarDays,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import {
  requestAwarenessBooking,
  SA_PROVINCES,
  ORGANISATION_TYPES,
  AUDIENCE_AGE_GROUPS,
} from "@/lib/api/awareness.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/crime-awareness")({
  head: () => ({
    meta: [
      {
        title:
          "Book a Crime Awareness Talk — Schools, Churches & Events | Nkadimeng Danny",
      },
      {
        name: "description",
        content:
          "Invite Nkadimeng Danny to deliver a crime-awareness session at your school, church, community event, or workplace. Practical content on rights, safety, scams, GBV, and the justice process.",
      },
      {
        property: "og:title",
        content: "Crime Awareness Talks — Book a Session",
      },
      {
        property: "og:description",
        content:
          "Crime-awareness sessions for schools, churches, communities and events across South Africa.",
      },
    ],
  }),
  component: CrimeAwarenessPage,
});

const TOPICS = [
  "Know your rights when arrested",
  "Stop & search — what police can and cannot do",
  "Cyber-crime, scams and phishing",
  "Gender-based violence (GBV) — reporting & support",
  "Protection orders & domestic violence",
  "Substance abuse and the law",
  "School safety & bullying",
  "Consumer scams (NCA / CPA basics)",
  "How the SA justice system actually works",
  "Reporting corruption safely",
];

const VENUES = [
  { I: GraduationCap, l: "Schools", d: "Primary, high school & TVET" },
  { I: Church, l: "Churches", d: "Faith-based community talks" },
  { I: Users, l: "Communities", d: "Stokvels, NGOs, sports clubs" },
  { I: Building2, l: "Corporate / Government", d: "Staff awareness sessions" },
];

function CrimeAwarenessPage() {
  const submit = useServerFn(requestAwarenessBooking);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [organisation, setOrganisation] = useState("");
  const [orgType, setOrgType] = useState<(typeof ORGANISATION_TYPES)[number]>("school");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [province, setProvince] = useState<(typeof SA_PROVINCES)[number]>("Gauteng");
  const [city, setCity] = useState("");
  const [venue, setVenue] = useState("");
  const [size, setSize] = useState("50");
  const [age, setAge] = useState<(typeof AUDIENCE_AGE_GROUPS)[number]>("mixed");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [topics, setTopics] = useState<string[]>([]);
  const [message, setMessage] = useState("");

  function toggleTopic(t: string) {
    setTopics((arr) => (arr.includes(t) ? arr.filter((x) => x !== t) : [...arr, t]));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submit({
        data: {
          organisation: organisation.trim(),
          organisation_type: orgType,
          contact_name: contactName.trim(),
          contact_email: contactEmail.trim(),
          contact_phone: contactPhone.trim(),
          province,
          city: city.trim(),
          venue: venue.trim(),
          audience_size: parseInt(size) || 0,
          audience_age_group: age,
          preferred_date: date || undefined,
          preferred_time: time,
          topics: topics.join(", "),
          message: message.trim(),
        },
      });
      setDone(true);
      toast.success("Request received", {
        description: "We'll respond within 1–2 business days.",
      });
    } catch (err: any) {
      toast.error("Could not submit", { description: err?.message });
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="container-narrow py-20 max-w-2xl text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h1 className="mt-4 font-display text-3xl text-navy">Request sent</h1>
        <p className="mt-3 text-muted-foreground">
          Thank you for inviting us. We&apos;ll review the details and respond
          within 1–2 business days to confirm availability and logistics.
        </p>
      </div>
    );
  }

  return (
    <div className="container-narrow py-12 space-y-12">
      <header className="max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] uppercase tracking-widest text-navy">
          <ShieldAlert className="h-3.5 w-3.5 text-gold" />
          Community outreach
        </div>
        <h1 className="mt-3 font-display text-4xl text-navy">
          Book a crime-awareness talk
        </h1>
        <p className="mt-4 text-foreground/80">
          Invite Nkadimeng Danny to deliver an interactive crime-awareness
          session at your school, church, community event, function or
          workplace. Sessions are practical, plain-language, and tailored to
          your audience.
        </p>
      </header>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {VENUES.map((v) => (
          <div key={v.l} className="rounded-2xl border border-border bg-card p-5">
            <v.I className="h-6 w-6 text-gold" />
            <p className="mt-3 font-display text-lg text-navy">{v.l}</p>
            <p className="mt-1 text-sm text-muted-foreground">{v.d}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-display text-xl text-navy">What we cover</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick the topics most relevant for your audience — or tell us what
          you&apos;re facing and we&apos;ll build a session around it.
        </p>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {TOPICS.map((t) => (
            <li key={t} className="flex items-start gap-2 text-sm">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              {t}
            </li>
          ))}
        </ul>
      </section>

      <form
        onSubmit={onSubmit}
        className="rounded-2xl border border-border bg-card p-6 shadow-soft space-y-5"
      >
        <div className="flex items-start gap-3">
          <CalendarDays className="mt-1 h-5 w-5 text-gold" />
          <div>
            <h2 className="font-display text-xl text-navy">Request a booking</h2>
            <p className="text-xs text-muted-foreground">
              We&apos;ll confirm logistics, format (in-person / virtual) and any
              cost contribution within 1–2 business days.
            </p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <Label>Organisation name</Label>
            <Input
              required
              value={organisation}
              onChange={(e) => setOrganisation(e.target.value)}
              placeholder="e.g. Hope Primary School"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Organisation type</Label>
            <Select value={orgType} onValueChange={(v) => setOrgType(v as any)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ORGANISATION_TYPES.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">
                    {t.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Contact name</Label>
            <Input
              required
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              required
              type="email"
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Phone</Label>
            <Input
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+27…"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Province</Label>
            <Select value={province} onValueChange={(v) => setProvince(v as any)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SA_PROVINCES.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>City / Town</Label>
            <Input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Venue</Label>
            <Input
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="e.g. School hall"
              className="mt-1"
            />
          </div>
          <div>
            <Label>Expected audience size</Label>
            <Input
              type="number"
              min="1"
              max="50000"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Audience age group</Label>
            <Select value={age} onValueChange={(v) => setAge(v as any)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {AUDIENCE_AGE_GROUPS.map((g) => (
                  <SelectItem key={g} value={g} className="capitalize">
                    {g.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Preferred date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Preferred time</Label>
            <Input
              value={time}
              onChange={(e) => setTime(e.target.value)}
              placeholder="e.g. 10:00"
              className="mt-1"
            />
          </div>
        </div>

        <div>
          <Label>Topics you want covered</Label>
          <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
            {TOPICS.map((t) => {
              const checked = topics.includes(t);
              return (
                <label
                  key={t}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border p-2 text-xs ${
                    checked ? "border-navy bg-navy/5" : "border-border bg-card"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleTopic(t)}
                    className="h-4 w-4 accent-navy"
                  />
                  {t}
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <Label>Anything else?</Label>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={4000}
            placeholder="Context, special focus areas, accessibility needs, language preferences…"
            className="mt-1 min-h-[100px]"
          />
        </div>

        <Button
          type="submit"
          disabled={submitting}
          className="bg-navy hover:bg-navy/90"
        >
          {submitting ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
          ) : (
            <>Send booking request</>
          )}
        </Button>
      </form>
    </div>
  );
}
